import { VOCABULARY_LOCALES, VOCABULARY_SLOTS } from "../enums/VocabularySlot";
import { OrganizationVocabulary } from "../models/Organization";
import { DEFAULT_VOCABULARY } from "./defaultVocabulary";

/** The interpolation variables a vocabulary yields: 7 slots × 4 forms. */
export type VocabularyVariables = Record<string, string>;

/** Languages whose mid-sentence form lower-cases the first letter. */
const LOWER_CASE_MID_SENTENCE = new Set(["en", "uk", "pl"]);

/** UI languages with Default words of their own but no organization renames (Arabic). */
const READ_ONLY_VOCABULARY_LOCALES: readonly string[] = ["ar"];

/** The vocabulary locale a UI language reads (de-AT → de, ar → ar, unknown → en). */
export const toVocabularyLocale = (language: string | null | undefined): string => {
  const base = (language ?? "").toLowerCase().split("-")[0];
  return VOCABULARY_LOCALES.includes(base) || READ_ONLY_VOCABULARY_LOCALES.includes(base) ? base : "en";
};

const capitalize = (slot: string): string => slot.charAt(0).toUpperCase() + slot.slice(1);

/** Lower-cases the first letter, but leaves acronyms ("PE class") alone. */
const toMidSentence = (word: string, locale: string): string => {
  if (!LOWER_CASE_MID_SENTENCE.has(locale)) return word;
  const second = word.charAt(1);
  if (second && second === second.toUpperCase() && second !== second.toLowerCase()) return word;
  return word.charAt(0).toLowerCase() + word.slice(1);
};

/**
 * Builds `{{member}}`, `{{members}}` (mid-sentence) and `{{Member}}`,
 * `{{Members}}` (sentence-initial, as stored) for every slot, from the
 * organization's resolved vocabulary for the language — or the Default
 * preset when there is no organization (yet).
 */
export const buildVocabularyVariables = (
  vocabulary: OrganizationVocabulary | null | undefined,
  language: string | null | undefined
): VocabularyVariables => {
  const locale = toVocabularyLocale(language);
  const variables: VocabularyVariables = {};
  for (const slot of VOCABULARY_SLOTS) {
    const term = vocabulary?.[locale]?.[slot] ?? DEFAULT_VOCABULARY[locale][slot];
    variables[slot] = toMidSentence(term.one, locale);
    variables[`${slot}s`] = toMidSentence(term.other, locale);
    variables[capitalize(slot)] = term.one;
    variables[`${capitalize(slot)}s`] = term.other;
  }
  return variables;
};
